// contexto-rotas.ts
// Lista canônica das rotas que honram o header `X-Empresa-Contexto`, e o algoritmo de
// casamento — fonte única dos dois interceptors (contador e Área Master).
//
// A UNIDADE DA LISTA É O PAR (método, rota), NUNCA O CAMINHO.
// `/nota/` estar na lista não libera `/nota/`: libera `GET /nota/`. `PUT`, `POST`, `PATCH` e
// `DELETE` sobre o mesmo caminho continuam de fora. Foi exatamente por casar só o caminho que o
// interceptor antigo injetava o header em `PUT /nota/cancelar/{id}` — e o backend, que só honra
// `GET /nota/`, descartava o header e cancelava a nota do CONTADOR com 200 OK.
//
// Espelho de `AuthorizationFilter.kt` (backend). A divergência entre as duas listas é permitida
// apenas via PARIDADE_EXCECOES, com motivo escrito — o `contexto-rotas.spec.ts` falha em qualquer
// outra divergência.
//
// Referência: vendas/docs/api/API_CONTRACT_allowlist-contexto-nfe-cotacao.md

export type MetodoHttp = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface ParContexto {
  readonly metodo: MetodoHttp;
  /** Terminado em `/` casa por PREFIXO ancorado no início; sem a barra, por IGUALDADE EXATA. */
  readonly padrao: string;
}

/** Par que existe no backend e, de propósito, NÃO é espelhado no interceptor. */
export interface ExcecaoParidade extends ParContexto {
  readonly motivo: string;
}

// ---------------------------------------------------------------------------
// 1. Espelho do backend — `ROTAS_CONTEXTO` do AuthorizationFilter.kt (58 pares)
//    GET 34 · POST 11 · PUT 10 · PATCH 3 · DELETE 0
// ---------------------------------------------------------------------------

export const ROTAS_CONTEXTO_BACKEND: ReadonlyArray<ParContexto> = [
  // Relatórios do cliente
  { metodo: 'GET', padrao: '/relatorios/relatorio-extrato-de-caixa' },
  { metodo: 'GET', padrao: '/relatorios/livro-de-vendas' },
  // Contas do cliente — somente LEITURA (AP-04 #10)
  { metodo: 'GET', padrao: '/conta' },
  { metodo: 'GET', padrao: '/conta/' },
  // Notas do cliente
  { metodo: 'GET', padrao: '/nota' },
  { metodo: 'GET', padrao: '/nota/buscar' },
  { metodo: 'GET', padrao: '/nota/' },
  { metodo: 'POST', padrao: '/nota' },
  { metodo: 'POST', padrao: '/nota/gerar-pdf-nota/' },
  { metodo: 'PUT', padrao: '/nota/transformar-nota-em-fatura/' },
  // Cotação do cliente (AP-04 #7/#8/#9)
  { metodo: 'GET', padrao: '/cotacao/cotacaoAtual' },
  { metodo: 'POST', padrao: '/cotacao' },
  { metodo: 'GET', padrao: '/cotacao' },
  { metodo: 'GET', padrao: '/cotacao/' },
  { metodo: 'PUT', padrao: '/cotacao/' },
  // Itens do cliente
  { metodo: 'GET', padrao: '/item' },
  { metodo: 'GET', padrao: '/item/' },
  { metodo: 'POST', padrao: '/item' },
  { metodo: 'PUT', padrao: '/item/' },
  // NFe do cliente (AP-04 #1..#6)
  { metodo: 'GET', padrao: '/nfe/consultar/' },
  { metodo: 'PATCH', padrao: '/nfe/cancelar/' },
  { metodo: 'GET', padrao: '/nfe/imprimir/' },
  { metodo: 'GET', padrao: '/nfe/eventos' },
  { metodo: 'GET', padrao: '/nfe/buscar-evento-envio-por-id-nfe/' },
  { metodo: 'GET', padrao: '/nfe/buscar-evento-retorno-por-id-nfe/' },
  { metodo: 'PATCH', padrao: '/nfe/inutilizar/' },
  { metodo: 'POST', padrao: '/nfe/transmitir/' },
  // Área do Contador
  { metodo: 'GET', padrao: '/contador/clientes' },
  { metodo: 'GET', padrao: '/contador/vendedores-venda' },
  // Configuração da empresa do cliente
  { metodo: 'GET', padrao: '/configuracaoEmpresa/contador' },
  { metodo: 'PUT', padrao: '/configuracaoEmpresa/contador' },
  { metodo: 'PATCH', padrao: '/configuracaoEmpresa/adicionar-certificado-digital' },
  // Timbrado
  { metodo: 'GET', padrao: '/timbrado' },
  { metodo: 'GET', padrao: '/timbrado/' },
  { metodo: 'POST', padrao: '/timbrado' },
  { metodo: 'PUT', padrao: '/timbrado/' },
  // Categoria / subcategoria / marca
  { metodo: 'GET', padrao: '/grupo' },
  { metodo: 'GET', padrao: '/grupo/' },
  { metodo: 'POST', padrao: '/grupo' },
  { metodo: 'PUT', padrao: '/grupo/' },
  { metodo: 'GET', padrao: '/subgrupo' },
  { metodo: 'GET', padrao: '/subgrupo/' },
  { metodo: 'POST', padrao: '/subgrupo' },
  { metodo: 'PUT', padrao: '/subgrupo/' },
  { metodo: 'GET', padrao: '/marca' },
  { metodo: 'GET', padrao: '/marca/' },
  { metodo: 'POST', padrao: '/marca' },
  { metodo: 'PUT', padrao: '/marca/' },
  // Unidades de medida
  { metodo: 'GET', padrao: '/unidaDeMedidaDeItemEmpresa' },
  { metodo: 'GET', padrao: '/unidaDeMedidaDeItemEmpresa/' },
  { metodo: 'POST', padrao: '/unidaDeMedidaDeItemEmpresa' },
  { metodo: 'PUT', padrao: '/unidadeMedidaItem/' },
  // Apoio do formulário de item
  { metodo: 'GET', padrao: '/impressora' },
  { metodo: 'GET', padrao: '/enums/generoItem' },
  // Clientes / fornecedores do cliente
  { metodo: 'GET', padrao: '/pessoas/buscar' },
  { metodo: 'GET', padrao: '/pessoas/' },
  { metodo: 'POST', padrao: '/pessoas' },
  { metodo: 'PUT', padrao: '/pessoas/' },
];

/** Espelho de `ROTAS_CONTEXTO_MASTER` do AuthorizationFilter.kt (6 pares — só cobrança). */
export const ROTAS_CONTEXTO_MASTER_BACKEND: ReadonlyArray<ParContexto> = [
  { metodo: 'GET', padrao: '/tpago/criar-link-de-subscricao/' },
  { metodo: 'GET', padrao: '/QrVendas/geradorQRCode/' },
  { metodo: 'GET', padrao: '/contratacao-usuarios/simular' },
  { metodo: 'GET', padrao: '/contratacao-usuarios/limite' },
  { metodo: 'POST', padrao: '/contratacao-usuarios/qr' },
  { metodo: 'POST', padrao: '/contratacao-usuarios/tpago' },
];

// ---------------------------------------------------------------------------
// 2. Exceções de paridade — pares do backend que o front NÃO injeta, de propósito
// ---------------------------------------------------------------------------

export const PARIDADE_EXCECOES: ReadonlyArray<ExcecaoParidade> = [
  {
    metodo: 'GET',
    padrao: '/contador/clientes',
    motivo:
      'O seletor de cliente lista a carteira do PRÓPRIO contador. Enviar o header NÃO é inócuo: ' +
      'o par está na allowlist do backend, então o header é honrado e `obterEmpresaId()` passa a ' +
      'devolver a empresa do CLIENTE — que não é contadora, e `listarClientes` responde 403 ' +
      '`contador_acesso_negado`. Rota sobre o contador, chamada sem contexto.',
  },
  {
    metodo: 'PUT',
    padrao: '/unidadeMedidaItem/',
    motivo:
      'Renomear unidade de medida. Nenhuma tela sob contexto faz esse PUT — a aba embutida de ' +
      'unidades só lista e vincula; quem usa a rota é a tela de configuração, fora das rotas de ' +
      'contexto. Par disponível ao ERP, ocioso na web.',
  },
];

// ---------------------------------------------------------------------------
// 3. Listas efetivamente usadas pelos interceptors
// ---------------------------------------------------------------------------

/** 56 pares = os 58 do backend menos as 2 exceções de paridade. */
export const ROTAS_CONTEXTO_CONTADOR: ReadonlyArray<ParContexto> = [
  { metodo: 'GET', padrao: '/relatorios/relatorio-extrato-de-caixa' },
  { metodo: 'GET', padrao: '/relatorios/livro-de-vendas' },
  // Contas do cliente: só LEITURA. `PUT /conta/{id}`, `POST /conta` e `DELETE /conta/{id}`
  // ficam de fora — sob contexto o backend responde 400 neles.
  { metodo: 'GET', padrao: '/conta' },
  { metodo: 'GET', padrao: '/conta/' },
  { metodo: 'GET', padrao: '/nota' },
  { metodo: 'GET', padrao: '/nota/buscar' },
  // Detalhe da nota, itens e estado da NFe — leitura da tela de fatura.
  { metodo: 'GET', padrao: '/nota/' },
  { metodo: 'POST', padrao: '/nota' },
  // Tela de fatura: imprimir e faturar a nota DO CLIENTE. Prefixos precisos de propósito —
  // `POST /nota/` ou `PUT /nota/` genéricos engoliriam `PUT /nota/cancelar/{id}`, que está
  // deliberadamente fora (o cancelamento é ocultado sob contexto).
  { metodo: 'POST', padrao: '/nota/gerar-pdf-nota/' },
  { metodo: 'PUT', padrao: '/nota/transformar-nota-em-fatura/' },
  { metodo: 'GET', padrao: '/cotacao/cotacaoAtual' },
  { metodo: 'POST', padrao: '/cotacao' },
  { metodo: 'GET', padrao: '/cotacao' },
  { metodo: 'GET', padrao: '/cotacao/' },
  { metodo: 'PUT', padrao: '/cotacao/' },
  { metodo: 'GET', padrao: '/item' },
  { metodo: 'GET', padrao: '/item/' },
  { metodo: 'POST', padrao: '/item' },
  { metodo: 'PUT', padrao: '/item/' },
  { metodo: 'GET', padrao: '/nfe/consultar/' },
  { metodo: 'PATCH', padrao: '/nfe/cancelar/' },
  { metodo: 'GET', padrao: '/nfe/imprimir/' },
  { metodo: 'GET', padrao: '/nfe/eventos' },
  { metodo: 'GET', padrao: '/nfe/buscar-evento-envio-por-id-nfe/' },
  { metodo: 'GET', padrao: '/nfe/buscar-evento-retorno-por-id-nfe/' },
  { metodo: 'PATCH', padrao: '/nfe/inutilizar/' },
  { metodo: 'POST', padrao: '/nfe/transmitir/' },
  // `GET /contador/clientes` NÃO entra — ver PARIDADE_EXCECOES.
  { metodo: 'GET', padrao: '/contador/vendedores-venda' },
  { metodo: 'GET', padrao: '/configuracaoEmpresa/contador' },
  { metodo: 'PUT', padrao: '/configuracaoEmpresa/contador' },
  { metodo: 'PATCH', padrao: '/configuracaoEmpresa/adicionar-certificado-digital' },
  { metodo: 'GET', padrao: '/timbrado' },
  { metodo: 'GET', padrao: '/timbrado/' },
  { metodo: 'POST', padrao: '/timbrado' },
  { metodo: 'PUT', padrao: '/timbrado/' },
  { metodo: 'GET', padrao: '/grupo' },
  { metodo: 'GET', padrao: '/grupo/' },
  { metodo: 'POST', padrao: '/grupo' },
  { metodo: 'PUT', padrao: '/grupo/' },
  { metodo: 'GET', padrao: '/subgrupo' },
  { metodo: 'GET', padrao: '/subgrupo/' },
  { metodo: 'POST', padrao: '/subgrupo' },
  { metodo: 'PUT', padrao: '/subgrupo/' },
  { metodo: 'GET', padrao: '/marca' },
  { metodo: 'GET', padrao: '/marca/' },
  { metodo: 'POST', padrao: '/marca' },
  { metodo: 'PUT', padrao: '/marca/' },
  { metodo: 'GET', padrao: '/unidaDeMedidaDeItemEmpresa' },
  { metodo: 'GET', padrao: '/unidaDeMedidaDeItemEmpresa/' },
  { metodo: 'POST', padrao: '/unidaDeMedidaDeItemEmpresa' },
  // `PUT /unidadeMedidaItem/` NÃO entra — ver PARIDADE_EXCECOES.
  { metodo: 'GET', padrao: '/impressora' },
  { metodo: 'GET', padrao: '/enums/generoItem' },
  { metodo: 'GET', padrao: '/pessoas/buscar' },
  { metodo: 'GET', padrao: '/pessoas/' },
  { metodo: 'POST', padrao: '/pessoas' },
  { metodo: 'PUT', padrao: '/pessoas/' },
];

/** Área Master — sem exceções: espelho integral dos 6 pares do backend. */
export const ROTAS_CONTEXTO_MASTER: ReadonlyArray<ParContexto> = [
  { metodo: 'GET', padrao: '/tpago/criar-link-de-subscricao/' },
  { metodo: 'GET', padrao: '/QrVendas/geradorQRCode/' },
  { metodo: 'GET', padrao: '/contratacao-usuarios/simular' },
  { metodo: 'GET', padrao: '/contratacao-usuarios/limite' },
  { metodo: 'POST', padrao: '/contratacao-usuarios/qr' },
  { metodo: 'POST', padrao: '/contratacao-usuarios/tpago' },
];

// ---------------------------------------------------------------------------
// 4. Algoritmo de casamento — byte a byte o `casaAllowlist` do backend
// ---------------------------------------------------------------------------

/**
 * Reduz a URL da request ao path que o backend enxerga:
 *
 * 1. descarta a query string;
 * 2. descarta o esquema e o host, quando a `apiBaseUrl` é absoluta (homolog/produção);
 * 3. remove UM único prefixo `/api` inicial — ele é do proxy do Angular em desenvolvimento e
 *    não chega ao backend (o Spring não tem `context-path`).
 *
 * `/apiario` NÃO perde prefixo nenhum: o `/api` só é removido quando é um segmento inteiro.
 */
export function normalizarPath(url: string): string {
  const semQuery = (url ?? '').split('?')[0].split('#')[0];
  const semHost = semQuery.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]*/i, '');
  return semHost.replace(/^\/api(?=\/|$)/, '');
}

/**
 * Casa o par (método, path) contra a lista. Idêntico ao `casaAllowlist` do
 * `AuthorizationFilter.kt`: padrão terminado em `/` casa por PREFIXO ANCORADO NO INÍCIO
 * (`startsWith`); sem a barra, por IGUALDADE EXATA.
 *
 * Nunca usar `includes` nem `endsWith`: `includes` faria `/relatorios/nota/x` casar `/nota/`, e
 * `endsWith` faria `/contador/clientes` casar `/conta`. Os dois defeitos já existiram aqui.
 */
export function casaAllowlist(
  rotas: ReadonlyArray<ParContexto>,
  metodo: string,
  url: string,
): boolean {
  const path = normalizarPath(url);
  const verbo = (metodo ?? '').toUpperCase();

  return rotas.some(
    (rota) =>
      rota.metodo === verbo &&
      (rota.padrao.endsWith('/') ? path.startsWith(rota.padrao) : path === rota.padrao),
  );
}
